/**
 * Group flat enhanced-match array into counterparty user groups, sub-grouped
 * by "the single pedal in a constellation." Pure function — input is the
 * already-perspective-swapped, already-enhanced match array; output is the
 * render-ready group structure.
 *
 * Anchor rule (content-driven, NOT perspective-driven):
 *   For each counterparty, partition matches into sub-groups where each
 *   sub-group is anchored on whichever side is "the 1" of a 1-to-many
 *   constellation. The "many" side then renders as rows beneath the anchor.
 *
 *   - 1 of yours ↔ N of theirs    → anchor = your pedal, rows = N of theirs
 *   - 1 of theirs ↔ N of yours    → anchor = their pedal, rows = N of yours
 *   - 1 of yours ↔ 1 of theirs    → anchor = their pedal, rows = 1 of yours
 *   - N of yours ↔ M of theirs    → produces multiple sub-groups, each
 *     individually following the rules above
 *
 * Algorithm:
 *   For each counterparty, count matches per myPedal.id and per matchedPedal.id.
 *   Greedily form anchor sub-groups: pick the pedal (either side) with the
 *   highest match count, claim all its matches as a sub-group anchored on it,
 *   remove those matches from the pool, repeat until pool is empty. Ties go to
 *   the counterparty side ("their pedal anchors") for consistency.
 *
 * Each sub-group carries an `anchorSide: 'mine' | 'theirs'` flag so the
 * renderer knows which pedal to style as the anchor (gold treatment when the
 * anchor is YOUR pedal — see styling notes in the card JSX).
 *
 * Filter rule (interpretation A): groups appear if ANY of their matches is in
 * `passingMatchIds`. When a group appears, ALL its visible matches render.
 *
 * Sort:
 *   - Top-level groups: 18V counterparty first, then by group's newest match
 *     createdAt desc ("freshest activity wins")
 *   - Sub-groups within a card: by sub-group's newest match createdAt desc
 *   - Rows within a sub-group: by createdAt desc (newest first)
 */
export function groupMatchesByUser(visibleMatches, passingMatchIds) {
  if (!visibleMatches || visibleMatches.length === 0) return [];

  // First pass: bucket by counterparty uid.
  const byUid = new Map();
  for (const m of visibleMatches) {
    const uid = m.counterpartyUid;
    if (!uid) continue;
    if (!byUid.has(uid)) {
      byUid.set(uid, {
        counterpartyUid: uid,
        counterpartyProfile: m.matchedPedalUser,
        matches: []
      });
    }
    byUid.get(uid).matches.push(m);
  }

  const groups = [];
  for (const bucket of byUid.values()) {
    const anyPassing = bucket.matches.some(m => passingMatchIds.has(m.matchId));
    if (!anyPassing) continue;

    // Greedy anchor-selection over the match pool for this counterparty.
    let pool = [...bucket.matches];
    const subGroups = [];

    while (pool.length > 0) {
      // Count matches per pedal id on both sides.
      const mineCounts = new Map();
      const theirsCounts = new Map();
      for (const m of pool) {
        const mineId = m.myPedal?.id;
        const theirsId = m.matchedPedal?.id;
        if (mineId) mineCounts.set(mineId, (mineCounts.get(mineId) || 0) + 1);
        if (theirsId) theirsCounts.set(theirsId, (theirsCounts.get(theirsId) || 0) + 1);
      }

      let bestSide = 'theirs';
      let bestId = null;
      let bestCount = 0;
      for (const [id, n] of theirsCounts.entries()) {
        if (n > bestCount) { bestCount = n; bestId = id; bestSide = 'theirs'; }
      }
      for (const [id, n] of mineCounts.entries()) {
        // Strict greater-than so theirs wins on ties.
        if (n > bestCount) { bestCount = n; bestId = id; bestSide = 'mine'; }
      }
      if (!bestId) break;

      // Claim all matches in the pool that touch this anchor pedal on the chosen side.
      const claimed = [];
      const remaining = [];
      for (const m of pool) {
        const sideId = bestSide === 'mine' ? m.myPedal?.id : m.matchedPedal?.id;
        if (sideId === bestId) claimed.push(m);
        else remaining.push(m);
      }
      pool = remaining;

      // Sort claimed by createdAt desc (newest first).
      claimed.sort((a, b) => {
        const at = a.createdAt?.toMillis?.() || 0;
        const bt = b.createdAt?.toMillis?.() || 0;
        return bt - at;
      });

      const anchorPedal = bestSide === 'mine' ? claimed[0].myPedal : claimed[0].matchedPedal;
      subGroups.push({
        anchorSide: bestSide,       // 'mine' | 'theirs'
        anchorPedal,
        matches: claimed,
        subGroupMaxCreatedMs: claimed[0]?.createdAt?.toMillis?.() || 0
      });
    }

    // Sub-groups within card: newest activity first.
    subGroups.sort((a, b) => b.subGroupMaxCreatedMs - a.subGroupMaxCreatedMs);

    groups.push({
      counterpartyUid: bucket.counterpartyUid,
      counterpartyProfile: bucket.counterpartyProfile,
      subGroups,
      allMatchIds: bucket.matches.map(m => m.matchId),
      totalMatchCount: bucket.matches.length,
      maxCreatedMs: subGroups[0]?.subGroupMaxCreatedMs || 0,
      isPromoted: bucket.counterpartyProfile?.membershipTier === 'premium_plus'
    });
  }

  // Top-level sort: promoted first, then newest activity.
  groups.sort((a, b) => {
    if (a.isPromoted !== b.isPromoted) return a.isPromoted ? -1 : 1;
    return b.maxCreatedMs - a.maxCreatedMs;
  });

  return groups;
}
